import { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, ShoppingCart, ArrowUpRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { PRODUCTS, CATEGORIES, fmtINR } from "@/data/catalog";
import { useCart } from "@/context/CartContext";

export default function Products() {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("category") || "all";
  const [q, setQ] = useState("");
  const { addItem } = useCart();

  const list = useMemo(() => {
    const term = q.toLowerCase();
    return PRODUCTS.filter((p) => {
      const mc = activeCat === "all" || p.categorySlug === activeCat;
      const mq = !term || p.name.toLowerCase().includes(term) || p.category.toLowerCase().includes(term);
      return mc && mq;
    });
  }, [q, activeCat]);

  const setCat = (slug) => { slug === "all" ? params.delete("category") : params.set("category", slug); setParams(params, { replace: true }); };

  const quickAdd = (e, p) => {
    e.preventDefault(); e.stopPropagation();
    addItem({ slug: p.slug, name: p.name, categorySlug: p.categorySlug, image: p.image, price: p.price, size: p.sizes[0], color: p.colors[0], quantity: p.moq });
    toast.success(`${p.name} added to cart`);
  };

  return (
    <div data-testid="products-page">
      <PageHeader overline="Our Products" title="Everything you need to package smarter." subtitle="Browse individual products across every category. Select your size, colour and quantity, add to cart and pay online, or request a wholesale quote." />

      <section className="mx-auto max-w-[1400px] px-5 lg:px-10">
        <div className="relative max-w-xl">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products..." data-testid="product-search" className="w-full rounded-full border border-border bg-card pl-11 pr-4 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-royal" />
        </div>
        <div className="mt-4 flex gap-2 overflow-x-auto no-scrollbar pb-1 scroll-smooth">
          <button onClick={() => setCat("all")} data-testid="filter-all" className={activeCat === "all" ? "whitespace-nowrap rounded-full px-5 py-2 text-sm font-medium border bg-navy text-white border-navy" : "whitespace-nowrap rounded-full px-5 py-2 text-sm font-medium border border-border hover:bg-secondary"}>All</button>
          {CATEGORIES.map((c) => (
            <button key={c.slug} onClick={() => setCat(c.slug)} data-testid={`filter-${c.slug}`} className={activeCat === c.slug ? "whitespace-nowrap rounded-full px-5 py-2 text-sm font-medium border bg-navy text-white border-navy" : "whitespace-nowrap rounded-full px-5 py-2 text-sm font-medium border border-border hover:bg-secondary"}>{c.name}</button>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-12">
        <p className="text-sm text-muted-foreground mb-6 font-mono-accent">{list.length} products</p>
        {list.length === 0 ? (
          <p className="py-20 text-center text-muted-foreground" data-testid="no-results">No products match your search.</p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {list.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 4) * 0.04}>
                <Link to={`/product/${p.slug}`} data-testid={`product-card-${p.slug}`} className="group flex flex-col h-full rounded-3xl border border-border bg-card overflow-hidden hover:shadow-xl hover:shadow-royal/5 hover:-translate-y-1 transition-all duration-300">
                  <div className="relative aspect-square overflow-hidden">
                    <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <button onClick={(e) => quickAdd(e, p)} data-testid={`quick-add-${p.slug}`} aria-label="Add to cart" className="absolute bottom-3 right-3 grid place-items-center h-10 w-10 rounded-full bg-navy text-white opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all hover:bg-royal"><ShoppingCart size={17} /></button>
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <span className="text-[11px] font-mono-accent uppercase tracking-wider text-muted-foreground">{p.category}</span>
                    <h3 className="mt-1 font-heading font-bold text-foreground group-hover:text-royal transition-colors leading-tight line-clamp-2">{p.name}</h3>
                    <div className="mt-auto pt-3 flex items-center justify-between">
                      <span className="font-mono-accent text-sm font-semibold text-foreground">{fmtINR(p.price)}</span>
                      <ArrowUpRight size={16} className="text-muted-foreground group-hover:text-royal transition-colors" />
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
